import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { formatTermsVersion } from '../lib/termsVersion';

const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || 'https://logfit.eu';

/**
 * Portail de (ré)acceptation des CGU — mandat de facturation.
 *
 * LogFit émet les relevés de ventes « au nom et pour le compte de »
 * l'organisateur. Ce mandat n'est opposable que s'il a été accepté : les CGU
 * du 2026-09-11 le portent, et chaque organisation doit avoir accepté la
 * version COURANTE (`organizations.terms_version`). Une organisation d'avant,
 * ou créée par un admin, n'a rien accepté : on le lui demande à la connexion,
 * une fois, avant qu'elle ne continue. La modale ne se ferme que par
 * l'acceptation — pas de croix, pas de clic à côté.
 */
const TermsAcceptanceGate: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, termsAcceptanceRequired, termsCurrentVersion, acceptTerms } = useAuth();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `null` = pas encore su : ne rien ouvrir sur une supposition.
  if (termsAcceptanceRequired !== true) return null;
  // Un participant n'a pas d'organisation ; un admin LogFit ne signe pas le
  // mandat d'un client depuis son propre compte.
  if (user?.role !== 'organizer') return null;

  const locale = (i18n.language || 'fr').slice(0, 2);
  const termsUrl = `${PUBLIC_SITE_URL}/${locale}/terms`;

  const handleAccept = async () => {
    setSaving(true);
    setError(null);
    try {
      await acceptTerms();
    } catch (e: any) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || t('termsGate.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        className="sm:max-w-lg [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {t('termsGate.title')}
          </DialogTitle>
          <DialogDescription>
            {t('termsGate.description', { version: formatTermsVersion(termsCurrentVersion, i18n.language) })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <p>{t('termsGate.mandateSummary')}</p>
          <a
            href={termsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-block underline underline-offset-4 text-foreground hover:text-primary"
          >
            {t('termsGate.readTerms')}
          </a>
        </div>

        <div className="flex items-start gap-3 rounded-md border border-border p-3">
          <Checkbox
            id="terms_gate_accept"
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            className="mt-0.5"
          />
          <Label htmlFor="terms_gate_accept" className="cursor-pointer leading-snug">
            {t('termsGate.checkbox')}
          </Label>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end">
          <Button onClick={handleAccept} disabled={!checked || saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('termsGate.accept')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TermsAcceptanceGate;
